import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { moved, Screen } from '@accounts/navigation';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { Button, Callout, CopyValue, Field, Form, Select } from '@beyond-js/ui/react';
import { Problem, useReauthenticate } from '@accounts/ui';

/**
 * Creating a personal access token, on a page of its own (`/tokens/new`, D52: it may ask the person to
 * confirm it is them, and it ends in a secret shown once), reached from the collection's **New token**.
 *
 * The form asks for a name, the scopes (`<product>:<scope>`, as each product documents them; Accounts
 * keeps no catalog of them and checks only that each product is one this installation serves), the
 * organization it may act in (one of the person's, or every one they belong to) and its lifetime.
 * Create token waits quietly while the name or the scopes are empty, still reachable, and a press puts the
 * caret in the first one (D183). Creating asks the person to confirm it is them when their sign-in is not
 * recent, then sends once more.
 *
 * Once created, the page shows the secret this once in place of the form, with its own Copy, and **Done**
 * returns to the collection; nothing keeps the secret after the page is left. A creation that got no
 * answer may have made the token: the outcome says so and its check opens the collection, which says what
 * is true, rather than sending again. **Cancel** returns to the collection, where focus lands on its title.
 */
export function TokenCreation() {
	const { text, client, identity, language } = usePresence();
	const navigate = useNavigate();
	const reauthenticate = useReauthenticate();
	const [failure, setFailure] = useState<unknown>();
	const [name, setName] = useState('');
	const [scopes, setScopes] = useState('');
	const [organization, setOrganization] = useState('');
	const [days, setDays] = useState('90');
	const [busy, setBusy] = useState(false);
	const [secret, setSecret] = useState<string>();
	const result = useRef<HTMLDivElement>(null);
	const back = () => navigate('/tokens', { state: moved });

	// The secret replaces the form: focus moves to it, so it is read first
	useEffect(() => {
		if (secret) result.current?.focus();
	}, [secret]);

	async function create() {
		setBusy(true);
		setFailure(undefined);
		const body = { name: name.trim(), scopes: scopes.split(/[\s,]+/).filter(Boolean), days: Number(days), ...(organization ? { organization } : {}) };
		const send = () => client.post<{ secret: string }>('/v1/account/tokens', body, { replay: false });
		try {
			let made;
			try {
				made = await send();
			} catch (error) {
				if (!(Refusal.is(error) && error.code === 'REAUTHENTICATION_REQUIRED' && (await reauthenticate()))) throw error;
				made = await send();
			}
			setSecret(made.secret);
		} catch (error) {
			if (!(Refusal.is(error) && error.quiet)) setFailure(error);
		} finally {
			setBusy(false);
		}
	}

	return (
		<Screen title={text('tokens.new')} width="form" crumbs={[{ label: text('nav.tokens'), href: '/tokens' }]}>
			{failure ? <Problem error={failure} check={{ label: text('tokens.check'), run: back }} /> : null}
			{secret ? (
				<div ref={result} tabIndex={-1}>
					<Callout tone="success" live title={text('tokens.created')}>
						{/* The secret, shown once: whole in its accessible name and tooltip, copied with its own button */}
						<div className="mt-2">
							<CopyValue value={secret} label={text('tokens.secret')} cut="middle" labels={CopyValue.labels[language]} />
						</div>
					</Callout>
					<div className="mt-4">
						<Button variant="primary" label={text('tokens.done')} onClick={back} />
					</div>
				</div>
			) : (
				<Form
					className="flex flex-col gap-4"
					onSubmit={event => {
						event.preventDefault();
						void create();
					}}
				>
					<Field label={text('tokens.name')} hint={text('tokens.name.help')}>
						<input value={name} maxLength={100} required onChange={event => setName(event.target.value)} />
					</Field>
					<Field label={text('tokens.scopes')} hint={text('tokens.scopes.help')}>
						<input value={scopes} spellCheck={false} autoCapitalize="none" required onChange={event => setScopes(event.target.value)} />
					</Field>
					<Field label={text('tokens.organization')} hint={organization ? undefined : text('tokens.organization.help')}>
						<Select
							value={organization}
							onChange={event => setOrganization(event.target.value)}
							options={[{ value: '', label: text('tokens.organization.any') }, ...(identity?.organizations ?? []).map(entry => ({ value: entry.organization, label: entry.name ?? entry.organization }))]}
						/>
					</Field>
					<Field label={text('tokens.days')}>
						<Select value={days} onChange={event => setDays(event.target.value)} options={['30', '90', '365'].map(value => ({ value, label: text('tokens.days.count', { count: Number(value) }) }))} />
					</Field>
					<div className="flex flex-wrap gap-2">
						<Button type="submit" variant="primary" busy={busy}>
							{text('tokens.create')}
						</Button>
						<Button variant="quiet" label={text('action.cancel')} onClick={back} />
					</div>
				</Form>
			)}
		</Screen>
	);
}
